import {ChevronRightIcon, MinusIcon, PlusIcon} from '@heroicons/react/outline';
import {useState} from 'react';

import {
  AfriexCurrencyCode,
  AfriexTransactionType,
} from '@afriex/core/domain/enums';

import {Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import {EUFlag} from '@/constants/countries';
import type {ProcessorCapabilityMatrix} from '@/services/processorService';
import type {AfriexCountry, AfriexCountryCode, AfriexCurrency} from '@/types';
import {mapCountryCodeToFlags} from '@/utils/countries';

import ProcessorSummary from './ProcessorSummary';
import type {ProcessorSettingTarget, RemoveProcessorRequest} from './types';
import type {SwitchedOffInUse} from './utils';
import {extractProcessorListFrom, findSwitchedOffInUse} from './utils';

const SWITCHED_OFF_LABEL = {dev: 'not live', retired: 'retired'} as const;

/**
 * Configured processors routing will skip in production, listed under the
 * column's default so the row says why it matched a switched-off filter.
 */
const SwitchedOffNotes = ({items}: {items: SwitchedOffInUse[]}) =>
  items.length ? (
    <ul className="mt-1 text-xs text-amber-600 dark:text-amber-400">
      {items.map((o) => (
        <li key={`${o.channel}-${o.processor}`}>
          (!) {o.processor.toUpperCase()} {SWITCHED_OFF_LABEL[o.status]} (
          {o.isCurrent ? 'current' : 'backup'},{' '}
          {o.channel.replace(/_/g, ' ').toLowerCase()})
        </li>
      ))}
    </ul>
  ) : null;

export interface ProcessorTableProps {
  /** One row per currency. A currency with no active country still gets a row. */
  items: AfriexCurrency[];
  countryList: AfriexCountry[];
  /** Representative active country per currency code, for the flag only. */
  countryCodeByCurrency: Record<string, AfriexCountryCode>;
  capabilityMatrix: ProcessorCapabilityMatrix;
  target: ProcessorSettingTarget;
  showProcessorUpdateForm: (currency: AfriexCurrency) => void;
  onRemoveProcessor: (req: RemoveProcessorRequest) => void;
}

const ProcessorTable = ({
  items,
  countryList,
  countryCodeByCurrency,
  capabilityMatrix,
  target,
  showProcessorUpdateForm,
  onRemoveProcessor,
}: ProcessorTableProps) => {
  return (
    <Table>
      <Thead>
        <Tr enableHover={false}>
          <Th className="w-20">&nbsp;</Th>
          <Th>Country</Th>
          <Th>Deposit</Th>
          <Th>Withdrawal</Th>
          <Th className="w-20">&nbsp;</Th>
        </Tr>
      </Thead>

      <TBody>
        {items.map((item) => (
          <ProcessorTableRow
            target={target}
            showProcessorUpdateForm={showProcessorUpdateForm}
            onRemoveProcessor={onRemoveProcessor}
            capabilityMatrix={capabilityMatrix}
            item={item}
            key={item?.code ?? item?.id}
            countryList={countryList}
            countryCode={countryCodeByCurrency?.[item?.code as string]}
          />
        ))}
      </TBody>
    </Table>
  );
};

const ProcessorTableRow = ({
  target,
  item,
  capabilityMatrix,
  countryCode,
  showProcessorUpdateForm,
  onRemoveProcessor,
  countryList,
}: {
  item: AfriexCurrency;
  countryCode: AfriexCountryCode;
  countryList: AfriexCountry[];
  target: 'user' | 'business' | 'api';
  capabilityMatrix: ProcessorCapabilityMatrix;
  showProcessorUpdateForm: ProcessorTableProps['showProcessorUpdateForm'];
  onRemoveProcessor: ProcessorTableProps['onRemoveProcessor'];
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const currencyCode = item?.code as AfriexCurrencyCode;
  const isEuroCurrency = item?.code === AfriexCurrencyCode.EUR;
  const flag = isEuroCurrency
    ? EUFlag
    : mapCountryCodeToFlags(countryList)[countryCode];
  const depositProcessors = extractProcessorListFrom(item?.[target]?.DEPOSIT);

  const withdrawalProcessors = extractProcessorListFrom(
    item?.[target]?.WITHDRAW,
  );
  const defaultDepositProcessor =
    depositProcessors?.[0]?.currentProcessor || 'N/A';
  const defaultWithdrawalProcessor =
    withdrawalProcessors?.[0]?.currentProcessor || 'N/A';
  const switchedOff = findSwitchedOffInUse(item, target, capabilityMatrix);

  return (
    <>
      <Tr>
        <Td isNumeric>
          <Button onClick={() => setIsExpanded(!isExpanded)} variant="unStyled">
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-gray-100 text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded && 'bg-cyan-50'
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
            </div>
          </Button>
        </Td>

        <Td className={`font-medium`}>
          {currencyCode || countryCode} {flag}
        </Td>

        <Td>
          {defaultDepositProcessor}
          <SwitchedOffNotes
            items={switchedOff.filter(
              (o) => o.type === AfriexTransactionType.DEPOSIT,
            )}
          />
        </Td>

        <Td>
          {defaultWithdrawalProcessor}
          <SwitchedOffNotes
            items={switchedOff.filter(
              (o) => o.type === AfriexTransactionType.WITHDRAW,
            )}
          />
        </Td>

        <Td>
          <div className="w-5 h-5 flex justify-center text-gray-700 dark:text-gray-300 hover:border rounded-lg hover:border-gray-300 hover:bg-cyan-50 hover:text-cyan-500">
            <ChevronRightIcon className="w-5 h-5" />
          </div>
        </Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={9} className="p-0 m-0">
            <div className="flex py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <ProcessorSummary
                item={item}
                target={target}
                capabilityMatrix={capabilityMatrix}
                countryCode={countryCode}
                showProcessorUpdateForm={showProcessorUpdateForm}
                onRemoveProcessor={onRemoveProcessor}
                countryList={countryList}
              />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default ProcessorTable;
